import React, { useEffect, useRef, useState, useCallback } from "react";
import { Video, MessageCircle, Send, Paperclip, Check, CheckCheck, Plus, LogIn, Copy, PhoneOff, Loader2, FileText, Users, Radio, Sparkles, Search, ArrowLeft, X, Trash2, Smile, Settings2 } from "lucide-react";
import DailyIframe from "@daily-co/daily-js";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ChatFx, BgPicker, getChatBg, Linkify } from "@/components/app/ChatExtras";
import VoiceRecorder from "@/components/app/VoiceRecorder";

/* ============ Estilos futuristas locales ============ */
export const Fx = () => (
  <>
  <ChatFx />
  <style>{`
    @keyframes com-pop { from { opacity:0; transform: translateY(10px) scale(.97);} to { opacity:1; transform: none;} }
    @keyframes com-pulse-ring { 0%{ box-shadow:0 0 0 0 rgba(var(--brand-light-rgb,30 58 138)/.45);} 70%{ box-shadow:0 0 0 10px rgba(var(--brand-light-rgb,30 58 138)/0);} 100%{ box-shadow:0 0 0 0 rgba(var(--brand-light-rgb,30 58 138)/0);} }
    @keyframes com-scan { 0%{ transform: translateX(-120%);} 100%{ transform: translateX(220%);} }
    .com-glass { background: rgba(255,255,255,.62); backdrop-filter: blur(20px) saturate(140%); -webkit-backdrop-filter: blur(20px) saturate(140%); }
    .com-glass-soft { background: rgba(255,255,255,.45); backdrop-filter: blur(14px) saturate(130%); -webkit-backdrop-filter: blur(14px); }
    .com-bubble-in { animation: com-pop .32s cubic-bezier(.2,.8,.2,1) both; }
    @keyframes com-typing { 0%,60%,100%{ transform: translateY(0); opacity:.45;} 30%{ transform: translateY(-4px); opacity:1;} }
    .com-typing-dot { width:7px; height:7px; border-radius:9999px; background: rgb(var(--brand-light-rgb,30 58 138)); display:inline-block; animation: com-typing 1.2s infinite ease-in-out; }
    .com-typing-dot:nth-child(2){ animation-delay:.18s; }
    .com-typing-dot:nth-child(3){ animation-delay:.36s; }
    .com-emoji-btn { transition: transform .12s ease; }
    .com-emoji-btn:hover { transform: scale(1.25); }
    .com-live-dot { animation: com-pulse-ring 2s infinite; }
    .com-neon-border { position: relative; }
    .com-neon-border::before { content:""; position:absolute; inset:0; border-radius: inherit; padding:1px; background: linear-gradient(135deg, rgb(var(--brand-light-rgb,30 58 138)/.6), transparent 55%, rgb(var(--brand-rgb,17 34 64)/.35)); -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events:none; }
    .com-scanline { position:absolute; top:0; left:0; height:100%; width:40%; background: linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent); animation: com-scan 3.4s ease-in-out infinite; }
    /* Chat en color sólido (sin transparencia) para que no se pierda el texto */
    [data-testid="chat-panel"] { background:#ffffff !important; backdrop-filter:none !important; -webkit-backdrop-filter:none !important; }
    [data-testid="chat-panel"] .com-glass { background:#ffffff !important; backdrop-filter:none !important; -webkit-backdrop-filter:none !important; }
    [data-testid="chat-panel"] .com-glass-soft { background:#eef1f5 !important; backdrop-filter:none !important; -webkit-backdrop-filter:none !important; }
    [data-testid="chat-panel"] .bg-gradient-to-b, [data-testid="chat-panel"] .bg-gradient-to-r { background-image:none !important; background-color:#ffffff !important; }
    [data-testid="chat-panel"] .border-white\\/40, [data-testid="chat-panel"] .border-white\\/50 { border-color:#e2e8f0 !important; }
  `}</style>
  </>
);

/* ---------------- Chat: contactos + DMs + grupos ---------------- */
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const roleColor = (r) => r === "docente" ? "from-emerald-400 to-teal-500" : r === "staff" ? "from-amber-400 to-orange-500" : "from-navy-light to-navy";
const Avatar = ({ src, name, role, size = "w-9 h-9", text = "text-[10px]", icon: Icon }) => {
  if (src) return <img src={`${API}${src}`} alt={name || ""} className={`shrink-0 ${size} rounded-full object-cover border border-white/60 bg-white`} />;
  return <span className={`shrink-0 ${size} rounded-full flex items-center justify-center text-white ${text} font-bold bg-gradient-to-br ${roleColor(role)}`}>{Icon ? <Icon className="w-4 h-4" /> : initials(name)}</span>;
};

function NewGroupDialog({ contacts, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [wa, setWa] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id]);
  const submit = async () => {
    if (!title.trim()) { toast.error("Ponle nombre al grupo"); return; }
    if (sel.length < 1) { toast.error("Elige al menos un integrante"); return; }
    setBusy(true);
    try { const { data } = await api.post("/chat/conversations/group", { title: title.trim(), member_ids: sel, whatsapp_link: wa.trim() || null }); onCreated(data); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="com-glass rounded-2xl border border-white/50 shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="new-group-dialog">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-navy">Nuevo grupo</h3><button onClick={onClose} className="text-nardo-400 hover:text-navy"><X className="w-5 h-5" /></button></div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nombre del grupo" data-testid="group-title" className="w-full com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy-light/20 mb-3" />
        <input value={wa} onChange={(e) => setWa(e.target.value)} placeholder="Liga de WhatsApp (opcional): https://chat.whatsapp.com/…" data-testid="group-wa" className="w-full com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy-light/20 mb-3" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light mb-1">Integrantes</div>
        <div className="max-h-56 overflow-y-auto space-y-1 mb-3">
          {contacts.length === 0 && <div className="text-sm text-nardo-400 py-2">No hay contactos.</div>}
          {contacts.map((c) => (
            <button key={c.id} onClick={() => toggle(c.id)} data-testid={`group-member-${c.id}`} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-xl border transition-colors ${sel.includes(c.id) ? "border-navy bg-navy/10" : "border-white/50 hover:border-navy-light/50"}`}>
              <span className={`w-7 h-7 rounded-full bg-gradient-to-br ${roleColor(c.role)} text-white flex items-center justify-center text-[9px] font-bold`}>{initials(c.name)}</span>
              <span className="text-sm text-navy flex-1 text-left truncate">{c.name}</span>
              {sel.includes(c.id) && <Check className="w-4 h-4 text-navy" />}
            </button>
          ))}
        </div>
        <button onClick={submit} disabled={busy} data-testid="group-create-submit" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-navy-light to-navy text-white py-2.5 rounded-xl font-semibold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Crear grupo</button>
      </div>
    </div>
  );
}

const EMOJI_SET = [
  "😀","😁","😂","🤣","😊","😍","😘","😎","🤩","🥳","😉","🙂","😅","😇","🤗","🤔","😴","😳","🥺","😢",
  "😭","😡","😱","🤯","😷","🤒","🤕","🤢","🤧","🥵","🥶","😈","👻","💀","🤖","🎃","😺","🙈","🙉","🙊",
  "👍","👎","👏","🙌","🙏","💪","👌","✌️","🤞","🤝","👋","🤙","☝️","✍️","💅","👀","🧠","❤️","🧡","💛",
  "💚","💙","💜","🖤","🤍","💔","❤️‍🔥","💯","✨","⭐","🌟","🔥","💥","🎉","🎊","🎈","🎁","🏆","🥇","📣",
  "✅","❌","⚠️","❓","❗","➡️","⏰","📅","📌","📎","📚","✏️","📝","💰","💵","🏫","🎓","🚸","🚌","☀️",
];
const STICKER_SET = ["🎉","👏","🔥","💯","❤️","👍","🙏","🥳","😂","😍","🤩","😎","🏆","🎓","📣","✅","⭐","💪","🌟","🍀","🎈","🤗","😢","🎊"];

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" data-testid="typing-dots" aria-label="escribiendo">
      <span className="com-typing-dot" /><span className="com-typing-dot" /><span className="com-typing-dot" />
    </span>
  );
}

function EmojiStickerPicker({ onEmoji, onSticker, onClose }) {
  const [tab, setTab] = useState("emoji");
  return (
    <>
      <div className="fixed inset-0 z-[55]" onClick={onClose} />
      <div className="absolute bottom-16 left-2 right-2 sm:left-3 sm:right-auto sm:w-80 z-[56] com-glass border border-white/60 rounded-2xl shadow-2xl p-3" data-testid="emoji-picker" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-2 mb-2">
          <button onClick={() => setTab("emoji")} data-testid="emoji-tab" className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${tab === "emoji" ? "bg-navy text-white" : "text-nardo-500 hover:bg-white/60"}`}>Emojis</button>
          <button onClick={() => setTab("sticker")} data-testid="sticker-tab" className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${tab === "sticker" ? "bg-navy text-white" : "text-nardo-500 hover:bg-white/60"}`}>Stickers</button>
        </div>
        {tab === "emoji" ? (
          <div className="grid grid-cols-8 gap-1 max-h-52 overflow-y-auto">
            {EMOJI_SET.map((e, i) => (
              <button key={i} onClick={() => onEmoji(e)} data-testid={`emoji-${i}`} className="com-emoji-btn text-xl leading-none p-1 rounded-lg hover:bg-white/60">{e}</button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-1.5 max-h-52 overflow-y-auto">
            {STICKER_SET.map((e, i) => (
              <button key={i} onClick={() => onSticker(e)} data-testid={`sticker-${i}`} className="com-emoji-btn text-4xl leading-none p-1.5 rounded-xl hover:bg-white/60">{e}</button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function GroupSettingsDialog({ conv, onClose, onSaved, onSendAviso }) {
  const [title, setTitle] = useState(conv.title || "");
  const [wa, setWa] = useState(conv.whatsapp_link || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim()) { toast.error("Ponle un nombre al grupo"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/chat/conversations/${conv.id}`, { title: title.trim(), whatsapp_link: wa.trim() });
      toast.success("Grupo actualizado");
      onSaved(data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="com-glass rounded-2xl border border-white/50 shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="group-settings-dialog">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-navy">Ajustes del grupo</h3><button onClick={onClose} className="text-nardo-400 hover:text-navy"><X className="w-5 h-5" /></button></div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Nombre del grupo</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="group-title-input" className="w-full com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 text-sm text-navy outline-none mb-3" />
        <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1 flex items-center gap-1">Liga del grupo de WhatsApp</label>
        <input value={wa} onChange={(e) => setWa(e.target.value)} placeholder="https://chat.whatsapp.com/…" data-testid="group-wa-input" className="w-full com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 text-sm text-navy outline-none mb-2" />
        <p className="text-[11px] text-nardo-400 mb-3">Pega la liga de invitación del grupo de WhatsApp. Aparecerá un botón para unirse y podrás enviar un aviso con la liga.</p>
        <div className="flex flex-col gap-2">
          <button onClick={save} disabled={busy} data-testid="group-save" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-navy-light to-navy text-white py-2.5 rounded-xl font-semibold disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar</button>
          {wa.trim() && (
            <button onClick={() => { onSendAviso(wa.trim()); onClose(); }} data-testid="group-send-aviso" className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white py-2.5 rounded-xl font-semibold"><Send className="w-4 h-4" /> Enviar aviso con la liga</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Chat({ heightClass = "h-[74vh]", compact = false }) {
  const [me, setMe] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // conversation view
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [typing, setTyping] = useState([]);
  const [q, setQ] = useState("");
  const [bgOpen, setBgOpen] = useState(false);
  const [bgTick, setBgTick] = useState(0);
  const [wasOpen, setWasOpen] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const activeRef = useRef(null);
  const lastTypeRef = useRef(0);
  activeRef.current = active;

  const scrollDown = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);

  const loadContacts = useCallback(async () => {
    try { const { data } = await api.get("/chat/contacts"); setContacts(data.contacts || []); setMe(data.me); } catch {}
  }, []);
  const loadConversations = useCallback(async () => {
    try { const { data } = await api.get("/chat/conversations"); setConversations(data.conversations || []); if (data.me) setMe(data.me); } catch {}
  }, []);
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try { const { data } = await api.get("/chat/messages", { params: { conversation_id: convId } });
      setMessages((prev) => { if (prev.length !== data.messages.length) scrollDown(); return data.messages; });
      setTyping(data.typing || []);
    } catch {}
  }, []);

  useEffect(() => { loadContacts(); loadConversations(); }, [loadContacts, loadConversations]);
  useEffect(() => {
    const slow = setInterval(() => loadConversations(), 3000);
    return () => clearInterval(slow);
  }, [loadConversations]);
  useEffect(() => {
    if (!active) { setTyping([]); return; }
    const fast = setInterval(() => { if (activeRef.current) loadMessages(activeRef.current.id); }, 1500);
    return () => clearInterval(fast);
  }, [active, loadMessages]);
  // Re-hidratar la conversación activa (título/liga/integrantes) desde la lista para reflejar cambios de otros miembros.
  useEffect(() => {
    if (!active) return;
    const fresh = conversations.find((c) => c.id === active.id);
    if (fresh && (fresh.title !== active.title || fresh.whatsapp_link !== active.whatsapp_link || (fresh.members?.length || 0) !== (active.members?.length || 0))) {
      setActive((a) => ({ ...a, title: fresh.title, whatsapp_link: fresh.whatsapp_link, members: fresh.members, other_role: fresh.other_role }));
    }
  }, [conversations]); // eslint-disable-line react-hooks/exhaustive-deps

  const notifyTyping = () => {
    if (!active) return;
    const now = Date.now();
    if (now - lastTypeRef.current > 1800) {
      lastTypeRef.current = now;
      api.post("/chat/typing", { conversation_id: active.id }).catch(() => {});
    }
  };
  const sendSticker = async (emoji) => {
    if (!active) return;
    setEmojiOpen(false);
    try { await api.post("/chat/messages", { conversation_id: active.id, sticker: emoji }); await loadMessages(active.id); loadConversations(); scrollDown(); }
    catch { toast.error("No se pudo enviar"); }
  };
  const sendAvisoWa = async (link) => {
    if (!active) return;
    try { await api.post("/chat/messages", { conversation_id: active.id, text: `📣 Únete a nuestro grupo de WhatsApp:\n${link}` }); await loadMessages(active.id); loadConversations(); scrollDown(); }
    catch { toast.error("No se pudo enviar el aviso"); }
  };

  const openConversation = async (conv) => { setActive(conv); setMessages([]); await loadMessages(conv.id); loadConversations(); scrollDown(); };
  const startDm = async (contact) => {
    try { const { data } = await api.post("/chat/conversations/dm", { contact_id: contact.id }); openConversation(data); }
    catch { toast.error("No se pudo abrir el chat"); }
  };

  const send = async (attachment = null) => {
    if (!active) return;
    const body = { conversation_id: active.id, text: text.trim(), attachment };
    if (!body.text && !attachment) return;
    setSending(true);
    try { await api.post("/chat/messages", body); setText(""); await loadMessages(active.id); loadConversations(); scrollDown(); }
    catch { toast.error("No se pudo enviar"); }
    finally { setSending(false); }
  };
  const onPickFile = async (e) => uploadAndSend(e.target.files?.[0], e);
  const uploadAndSend = async (f, e) => {
    if (e) e.target.value = "";
    if (!f || !active) return;
    if (f.size > 60 * 1024 * 1024) { toast.error("Máximo 60 MB"); return; }
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      await send(data);
    } catch { toast.error("No se pudo subir el archivo"); }
    finally { setUploading(false); }
  };

  const convContactIds = new Set(conversations.filter((c) => c.type === "dm").flatMap((c) => c.members.map((m) => m.id)));
  const filteredContacts = contacts.filter((c) => !q.trim() || c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()));

  const deleteConversation = async () => {
    if (!active) return;
    if (!window.confirm("¿Borrar este chat? Se eliminarán todos los mensajes para todos los participantes.")) return;
    try { await api.delete(`/chat/conversations/${active.id}`); toast.success("Chat borrado"); setActive(null); loadConversations(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };
  const filteredConvs = conversations.filter((c) => !q.trim() || (c.title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className={`com-glass com-neon-border rounded-2xl flex ${heightClass} overflow-hidden shadow-2xl shadow-navy/10 uai-fade-up`} data-testid="chat-panel">
      {/* Sidebar */}
      <div key={active ? "list-hidden" : "list"} className={`${!active && wasOpen ? "zv-back " : ""}${compact ? (active ? "hidden" : "flex") : (active ? "hidden md:flex" : "flex")} w-full ${compact ? "" : "md:w-72 lg:w-80"} shrink-0 flex-col border-r border-white/40 bg-gradient-to-b from-white/40 to-transparent`}>
        <div className="p-3 border-b border-white/40 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-nardo-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" data-testid="chat-search" className="w-full com-glass-soft border border-white/50 rounded-xl pl-8 pr-3 py-2 text-sm text-navy outline-none" />
            </div>
            <button onClick={() => setGroupOpen(true)} data-testid="chat-new-group" title="Nuevo grupo" className="p-2 bg-gradient-to-br from-navy-light to-navy text-white rounded-xl"><Users className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] text-nardo-500 leading-snug">Chatea directo con tu equipo del colegio. No necesitas agregar cuentas: aparecen aquí automáticamente.</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredConvs.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-navy-light">Conversaciones</div>}
          {filteredConvs.map((c) => (
            <button key={c.id} onClick={() => openConversation(c)} data-testid={`conv-${c.id}`} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/50 transition-colors ${active?.id === c.id ? "bg-white/60" : ""}`}>
              <span className="shrink-0">{c.type === "group" ? <span className="w-9 h-9 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-fuchsia-500 to-purple-600"><Users className="w-4 h-4" /></span> : <Avatar src={c.avatar} name={c.title} role={c.other_role} />}</span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-navy truncate">{c.title}</span>{c.unread > 0 && <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>}</span>
                <span className="block text-[11px] text-nardo-500 truncate">{c.last_text || (c.type === "group" ? "Grupo" : "Sin mensajes aún")}</span>
              </span>
            </button>
          ))}
          <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-navy-light">Tu equipo</div>
          {filteredContacts.length === 0 && <div className="px-3 py-2 text-xs text-nardo-400">Aún no hay docentes ni personal en tu colegio. Créalos en Docentes/Personal y aparecerán aquí.</div>}
          {filteredContacts.map((c) => (
            <button key={c.id} onClick={() => startDm(c)} data-testid={`contact-${c.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/50 transition-colors">
              <Avatar src={c.avatar} name={c.name} role={c.role} size="w-8 h-8" text="text-[9px]" />
              <span className="flex-1 min-w-0"><span className="block text-sm text-navy truncate">{c.name}</span><span className="block text-[10px] text-nardo-400 truncate">{c.role === "docente" ? "Docente" : c.role === "staff" ? "Administrativo" : "Dirección"}</span></span>
            </button>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div key={active?.id || "none"} className={`${active ? "zv-enter " : ""}${compact ? (active ? "flex" : "hidden") : (active ? "flex" : "hidden md:flex")} flex-1 flex-col min-w-0 relative`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 text-nardo-500 p-6">
            <div className="w-14 h-14 rounded-2xl com-glass-soft flex items-center justify-center"><MessageCircle className="w-6 h-6 text-navy-light" /></div>
            <div className="text-sm">Elige un contacto o conversación<br />para empezar a chatear.</div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-white/40 flex items-center gap-3 bg-gradient-to-r from-white/50 to-transparent">
              <button onClick={() => { setActive(null); setWasOpen(true); setBgOpen(false); }} className={`${compact ? "" : "md:hidden"} text-navy`} data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></button>
              <span className="shrink-0">{active.type === "group" ? <span className="w-9 h-9 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-fuchsia-500 to-purple-600"><Users className="w-4 h-4" /></span> : <Avatar src={active.avatar} name={active.title} role={active.other_role} />}</span>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-navy leading-tight truncate">{active.title}</div>
                <div className="text-[11px] text-navy-light truncate h-4">
                  {typing.length > 0
                    ? (active.type === "group" ? `${typing[0].name} está escribiendo…` : "escribiendo…")
                    : (active.type === "group" ? `${active.members.length} integrantes` : (active.other_role === "docente" ? "Docente" : active.other_role === "staff" ? "Administrativo" : "Dirección"))}
                </div>
              </div>
              {active.type === "group" && active.whatsapp_link && (
                <a href={active.whatsapp_link} target="_blank" rel="noreferrer" data-testid="chat-wa-join" title="Unirse al grupo de WhatsApp" className="shrink-0 w-9 h-9 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0012.04 2zm0 18.15c-1.48 0-2.93-.4-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 01-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 012.42 5.83c0 4.54-3.7 8.24-8.25 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.22.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.73 2.64 4.19 3.7.59.25 1.04.4 1.4.51.59.19 1.12.16 1.54.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/></svg>
                </a>
              )}
              {active.type === "group" && (
                <button onClick={() => setSettingsOpen(true)} data-testid="chat-group-settings" title="Ajustes del grupo" className="shrink-0 w-9 h-9 rounded-full com-glass-soft border border-white/50 text-navy flex items-center justify-center hover:bg-white/70 transition-colors"><Settings2 className="w-4 h-4" /></button>
              )}
              <button onClick={() => setBgOpen((o) => !o)} data-testid="chat-bg-btn" title="Fondo del chat" className="shrink-0 w-9 h-9 rounded-full com-glass-soft border border-white/50 text-navy flex items-center justify-center hover:bg-white/70 transition-colors"><Sparkles className="w-4 h-4" /></button>
              <button onClick={deleteConversation} data-testid="chat-delete-conv" title="Borrar chat" className="shrink-0 w-9 h-9 rounded-full com-glass-soft border border-white/50 text-red-500 flex items-center justify-center hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
            {bgOpen && <BgPicker convId={active.id} onChange={() => setBgTick((t) => t + 1)} onClose={() => setBgOpen(false)} />}
            <div className="flex-1 overflow-y-auto px-4 py-5 space-y-2.5" data-testid="chat-messages" data-bg={bgTick} style={{ background: getChatBg(active.id).css }}>
              {messages.length === 0 && <div className="text-center text-sm text-nardo-400 py-10">No hay mensajes. ¡Escribe el primero!</div>}
              {messages.map((m) => {
                const mine = me && m.sender_id === me.id;
                const readByOthers = (m.read_by || []).filter((x) => x !== m.sender_id).length > 0;
                return (
                  <div key={m.id} className={`flex com-bubble-in ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                    {!mine && <span className="shrink-0 self-end mr-2"><Avatar src={m.sender_avatar} name={m.sender_name} role={m.sender_role} size="w-7 h-7" text="text-[9px]" /></span>}
                    {m.sticker && !m.text && !m.attachment ? (
                      <div className="max-w-[76%] flex flex-col" data-testid={`chat-sticker-${m.id}`}>
                        {!mine && active.type === "group" && <div className="text-[10px] font-bold text-navy-light mb-0.5">{m.sender_name}</div>}
                        <div className="text-6xl leading-none select-none">{m.sticker}</div>
                        <div className={`text-[9px] mt-0.5 flex items-center gap-1 ${mine ? "justify-end text-nardo-400" : "text-nardo-400"}`}>
                          {new Date(m.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {mine && (readByOthers ? <CheckCheck className="w-3.5 h-3.5 text-sky-400" /> : <Check className="w-3.5 h-3.5" />)}
                        </div>
                      </div>
                    ) : (
                    <div className={`zv-bub max-w-[76%] rounded-2xl px-3.5 py-2 ${mine ? "zv-bub-mine rounded-br-md" : "zv-bub-other rounded-bl-md"}`}>
                      {!mine && active.type === "group" && <div className="text-[10px] font-bold text-navy-light mb-0.5">{m.sender_name}</div>}
                      {m.attachment && (
                        <div className="mb-1">
                          {m.attachment.kind === "image" && <img src={`${API}${m.attachment.url}`} alt={m.attachment.name} className="rounded-xl max-h-56 object-cover" />}
                          {m.attachment.kind === "audio" && <audio src={`${API}${m.attachment.url}`} controls preload="metadata" className="w-60 max-w-full h-10" data-testid={`chat-audio-${m.id}`} />}
                          {m.attachment.kind === "video" && <video src={`${API}${m.attachment.url}`} controls className="rounded-xl max-h-56 w-full" />}
                          {m.attachment.kind === "file" && <a href={`${API}${m.attachment.url}`} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 text-xs underline ${mine ? "text-white" : "text-navy"}`}><FileText className="w-4 h-4" /> {m.attachment.name}</a>}
                        </div>
                      )}
                      {m.sticker && <div className="text-4xl leading-none mb-0.5">{m.sticker}</div>}
                      {m.text && <div className="text-sm whitespace-pre-wrap break-words leading-relaxed"><Linkify text={m.text} /></div>}
                      <div className={`text-[9px] mt-0.5 flex items-center gap-1 justify-end ${mine ? "text-white/70" : "text-nardo-400"}`}>
                        {new Date(m.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {mine && (readByOthers ? <CheckCheck className="w-3.5 h-3.5 text-sky-300" data-testid="msg-read" /> : <Check className="w-3.5 h-3.5" data-testid="msg-sent" />)}
                      </div>
                    </div>
                    )}
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            {typing.length > 0 && (
              <div className="px-4 pb-1 flex justify-start" data-testid="typing-indicator">
                <div className="com-glass border border-white/50 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow"><TypingDots /></div>
              </div>
            )}
            <div className="relative border-t border-white/40 p-3 flex items-end gap-2 bg-gradient-to-r from-white/50 to-transparent">
              {emojiOpen && <EmojiStickerPicker onEmoji={(e) => setText((t) => t + e)} onSticker={sendSticker} onClose={() => setEmojiOpen(false)} />}
              <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" data-testid="chat-file-input" />
              <button onClick={() => setEmojiOpen((o) => !o)} data-testid="chat-emoji" title="Emojis y stickers" className={`shrink-0 p-2.5 rounded-full hover:bg-white/60 transition-colors ${emojiOpen ? "text-navy bg-white/60" : "text-nardo-500 hover:text-navy"}`}><Smile className="w-5 h-5" /></button>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-attach" className="shrink-0 p-2.5 text-nardo-500 hover:text-navy rounded-full hover:bg-white/60 transition-colors">{uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}</button>
              <textarea value={text} onChange={(e) => { setText(e.target.value); notifyTyping(); }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); send(); } }} rows={2} placeholder="Escribe un mensaje…" data-testid="chat-input" className="flex-1 resize-none com-glass-soft border border-white/50 rounded-2xl px-4 py-2.5 text-base text-navy placeholder:text-nardo-400 outline-none focus:border-navy-light/60 focus:ring-2 focus:ring-navy-light/20 transition-all max-h-28" />
              <div className="shrink-0 flex flex-col items-center gap-1.5">
                <button onClick={() => send()} disabled={sending || !text.trim()} data-testid="chat-send" title="Enviar mensaje" className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-navy-light to-navy text-white rounded-full hover:shadow-lg hover:scale-105 active:scale-95 disabled:opacity-40 transition-all">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
                <VoiceRecorder disabled={uploading || sending} onSend={(f) => uploadAndSend(f)} />
              </div>
            </div>
          </>
        )}
      </div>

      {groupOpen && <NewGroupDialog contacts={contacts} onClose={() => setGroupOpen(false)} onCreated={(c) => { loadConversations(); openConversation(c); }} />}
      {settingsOpen && active?.type === "group" && (
        <GroupSettingsDialog conv={active} onClose={() => setSettingsOpen(false)}
          onSaved={(c) => { setActive(c); loadConversations(); }}
          onSendAviso={sendAvisoWa} />
      )}
    </div>
  );
}

/* ---------------- Sala de video Daily.co en pantalla completa ---------------- */
function DailyRoom({ meeting, displayName, onExit }) {
  const wrapRef = useRef(null);
  const callRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let disposed = false;
    if (!meeting.room_url) { setError("Esta reunión no tiene una sala de video válida."); setLoading(false); return; }
    // Daily solo permite una instancia a la vez: destruye cualquier previa.
    try { DailyIframe.getCallInstance()?.destroy?.(); } catch { /* noop */ }
    let frame;
    try {
      frame = DailyIframe.createFrame(wrapRef.current, {
        showLeaveButton: true,
        showFullscreenButton: true,
        iframeStyle: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "0" },
      });
    } catch { setError("No se pudo iniciar el video."); setLoading(false); return; }
    callRef.current = frame;
    frame.on("joined-meeting", () => { if (!disposed) setLoading(false); });
    frame.on("left-meeting", () => onExit());
    frame.on("error", () => { if (!disposed) { setError("No se pudo conectar a la reunión. Verifica tu conexión."); setLoading(false); } });
    frame.join({ url: meeting.room_url, userName: displayName || "Invitado" })
      .catch(() => { if (!disposed) { setError("No se pudo conectar a la reunión."); setLoading(false); } });
    return () => {
      disposed = true;
      try { callRef.current?.destroy?.(); } catch { /* noop */ }
      callRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.room_url]);

  const leave = () => {
    try { callRef.current?.leave?.(); } catch { /* noop */ }
    try { callRef.current?.destroy?.(); } catch { /* noop */ }
    callRef.current = null;
    onExit();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-navy-ink flex flex-col" data-testid="meeting-live">
      <div className="flex items-center justify-between px-4 py-2.5 bg-navy-ink/95 border-b border-white/10 shrink-0">
        <div className="text-sm text-white font-semibold truncate flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-red-500 com-live-dot shrink-0" />
          <span className="truncate">{meeting.title}</span>
          <span className="font-mono tracking-widest text-white/60 hidden sm:inline">· {meeting.code}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {meeting.room_url && (
            <a href={meeting.room_url} target="_blank" rel="noreferrer" data-testid="meeting-open-tab" className="text-[11px] text-white/90 border border-white/25 hover:bg-white/10 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors"><LogIn className="w-3 h-3" /> <span className="hidden sm:inline">Abrir en pestaña</span></a>
          )}
          <button onClick={() => { navigator.clipboard?.writeText(meeting.code); toast.success("Código copiado"); }} className="text-[11px] text-white/90 border border-white/25 hover:bg-white/10 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors"><Copy className="w-3 h-3" /> <span className="hidden sm:inline">Código</span></button>
          <button onClick={leave} data-testid="meeting-leave" className="text-[12px] bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 py-1.5 inline-flex items-center gap-1.5 font-semibold transition-colors"><PhoneOff className="w-4 h-4" /> Salir</button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0">
        {loading && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 gap-3">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Conectando a la reunión…</span>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-4 px-6 text-center">
            <span className="text-sm text-white/80">{error}</span>
            {meeting.room_url && (
              <a href={meeting.room_url} target="_blank" rel="noreferrer" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1.5"><LogIn className="w-4 h-4" /> Abrir la reunión en una pestaña nueva</a>
            )}
            <button onClick={onExit} className="bg-white/10 hover:bg-white/20 border border-white/25 text-white rounded-lg px-4 py-2 text-sm">Volver</button>
          </div>
        )}
        <div ref={wrapRef} className="absolute inset-0" data-testid="daily-frame" />
      </div>
    </div>
  );
}

/* ---------------- Reunión digital (Jitsi) ---------------- */
function Reunion() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [active, setActive] = useState(null); // { jitsi_room, title, code }
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/meetings"); setMeetings(data.meetings || []); } catch {} };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/meetings", { title: title.trim() || "Reunión" }); setTitle(""); load(); setActive(data); toast.success(`Reunión creada. Código: ${data.code}`); }
    catch { toast.error("No se pudo crear la reunión"); }
    finally { setBusy(false); }
  };
  const join = async (e) => {
    e?.preventDefault?.();
    if (!joinCode.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/meetings/join", { code: joinCode.trim() }); setActive(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Código inválido"); }
    finally { setBusy(false); }
  };
  const enter = (m) => setActive(m);
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Código copiado"); };
  const teamRoom = async () => {
    setBusy(true);
    try { const { data } = await api.post("/meetings/team"); load(); setActive(data); }
    catch { toast.error("No se pudo abrir la sala del equipo"); }
    finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar la reunión "${m.title}"?`)) return;
    try { await api.post(`/meetings/${m.id}/end`); toast.success("Reunión eliminada"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  if (active) {
    return (
      <DailyRoom
        meeting={active}
        displayName={user?.name || user?.staff_name || "Invitado"}
        onExit={() => { setActive(null); load(); }}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 uai-fade-up" data-testid="meeting-lobby">
      {/* crear */}
      <div className="com-glass com-neon-border rounded-2xl p-6 relative overflow-hidden shadow-xl shadow-navy/10">
        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden"><span className="com-scanline" /></div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-navy-light to-navy flex items-center justify-center text-white shadow-lg"><Video className="w-5 h-5" /></div>
          <h2 className="font-display font-bold text-xl text-navy">Iniciar reunión</h2>
        </div>
        <p className="text-sm text-nardo-600 mb-4">Crea una sala HD y comparte el código con los docentes para que se conecten al instante.</p>
        <button onClick={teamRoom} disabled={busy} data-testid="meeting-team" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl font-semibold disabled:opacity-50 transition-all active:scale-[.98] mb-3">
          <Users className="w-4 h-4" /> Entrar a la sala del equipo
        </button>
        <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-400 text-center mb-3">o crea una reunión nueva</div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (ej. Junta de consejo)" data-testid="meeting-title" className="w-full com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 text-sm text-navy placeholder:text-nardo-400 mb-3 outline-none focus:ring-2 focus:ring-navy-light/20 focus:border-navy-light/60 transition-all" />
        <button onClick={create} disabled={busy} data-testid="meeting-create" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-navy-light to-navy hover:shadow-lg hover:shadow-navy/30 text-white py-2.5 rounded-xl font-semibold disabled:opacity-50 transition-all active:scale-[.98]">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear reunión
        </button>
      </div>

      {/* entrar */}
      <div className="com-glass com-neon-border rounded-2xl p-6 shadow-xl shadow-navy/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl com-glass-soft border border-white/50 flex items-center justify-center text-navy-light"><LogIn className="w-5 h-5" /></div>
          <h2 className="font-display font-bold text-xl text-navy">Entrar con código</h2>
        </div>
        <form onSubmit={join} className="flex gap-2 mb-4">
          <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="CÓDIGO" data-testid="meeting-join-code" className="flex-1 com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 text-sm font-mono tracking-[0.3em] uppercase text-navy placeholder:text-nardo-400 outline-none focus:ring-2 focus:ring-navy-light/20 focus:border-navy-light/60 transition-all" />
          <button type="submit" disabled={busy} data-testid="meeting-join" className="bg-gradient-to-br from-navy-light to-navy hover:shadow-lg hover:shadow-navy/30 text-white px-5 rounded-xl font-semibold disabled:opacity-50 transition-all active:scale-95">Entrar</button>
        </form>
        <div className="text-[10px] uppercase tracking-[0.25em] text-navy-light mb-2 flex items-center gap-1.5"><Radio className="w-3 h-3 text-emerald-500" /> Reuniones activas</div>
        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
          {meetings.length === 0 && <div className="text-sm text-nardo-400 py-2">No hay reuniones activas.</div>}
          {meetings.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-2 com-glass-soft border border-white/50 rounded-xl px-3.5 py-2.5 hover:border-navy-light/40 transition-colors" data-testid={`meeting-row-${m.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-navy font-semibold truncate">{m.title}</div>
                <div className="text-[11px] text-nardo-500">Código <span className="font-mono tracking-widest text-navy-light">{m.code}</span> · {m.host_name}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => copy(m.code)} title="Copiar código" className="text-nardo-500 hover:text-navy p-1.5 rounded-lg hover:bg-white/60 transition-colors"><Copy className="w-4 h-4" /></button>
                <button onClick={() => remove(m)} data-testid={`meeting-delete-${m.id}`} title="Eliminar reunión" className="text-red-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
                <button onClick={() => enter(m)} data-testid={`meeting-enter-${m.id}`} className="text-[11px] bg-gradient-to-br from-navy-light to-navy text-white rounded-lg px-3 py-1.5 hover:shadow-md transition-all active:scale-95">Entrar</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Comunicacion({ embedded = false }) {
  const [tab, setTab] = useState("reunion");
  return (
    <div className="max-w-6xl mx-auto" data-testid="comunicacion-page">
      <Fx />
      {!embedded && (
      <div className="mb-6 uai-fade-up">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2"><span className="w-8 h-px bg-gradient-to-r from-navy-light to-transparent" /> <Sparkles className="w-3.5 h-3.5" /> Comunicación interna</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy flex items-center gap-3">
          {tab === "reunion" ? <><Video className="w-8 h-8 text-navy-light" strokeWidth={1.5} /> Reunión digital</> : <><MessageCircle className="w-8 h-8 text-navy-light" strokeWidth={1.5} /> Chat del equipo</>}
        </h1>
      </div>
      )}
      <div className="inline-flex gap-1 mb-6 com-glass p-1 rounded-2xl border border-white/50 shadow-md">
        <button onClick={() => setTab("reunion")} data-testid="com-tab-reunion" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "reunion" ? "bg-gradient-to-br from-navy-light to-navy text-white shadow-lg shadow-navy/20" : "text-nardo-600 hover:text-navy"}`}><Video className="w-4 h-4" /> Reunión digital</button>
        <button onClick={() => setTab("chat")} data-testid="com-tab-chat" className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "chat" ? "bg-gradient-to-br from-navy-light to-navy text-white shadow-lg shadow-navy/20" : "text-nardo-600 hover:text-navy"}`}><MessageCircle className="w-4 h-4" /> Chat</button>
      </div>
      {tab === "reunion" ? <Reunion /> : <Chat />}
    </div>
  );
}
